{
  "title": "How to Declare a Conflict of Interest",
  "description": "Self-declare a conflict of interest when you have a relationship with a submission's participants that could affect scoring fairness.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, that submission's card stays right where it was in your assignment list, now marked \"Recused\" and no longer scorable; the server-side score-save/submit and file-access APIs reject it too.",
  "roleNote": "Judge",
  "prerequisites": [
    "You're signed in as a judge",
    "You can see the submission in your assignment list"
  ],
  "pathLabel": "Judge Workspace → Assignment List",
  "steps": [
    {
      "title": "1. Decide whether it's a conflict of interest",
      "description": "Whenever you have a relationship with a submission's participants that could affect scoring fairness (e.g. you advised that team, or have a family/employment relationship), you must declare it; whether it counts as a conflict is your own judgment call based on the actual relationship — the system does not detect this automatically."
    },
    {
      "title": "2. Click \"Declare conflict of interest\"",
      "description": "Next to that submission in your assignment list, click \"Declare conflict of interest\", fill in a reason, and submit."
    },
    {
      "title": "3. Confirm it took effect",
      "description": "The submission's card stays in your assignment list, but now carries a red \"Recused\" badge, the \"Declare conflict of interest\" button is gone, and the scoring link is replaced with the note \"You can no longer score this submission.\""
    }
  ],
  "afterward": [
    "The submission's card remains visible in your assignment list — it is not removed or hidden, only labeled \"Recused\"",
    "You can no longer open that submission's scoring page to save or submit a score — the server rejects every save/submit attempt in real time (`409 CONFLICT_OF_INTEREST`)",
    "You can no longer download that submission's files — the server rejects every file-access attempt in real time",
    "If you already had a locked score before the conflict was recorded, that score is automatically excluded retroactively when the organizer calculates results, and is not counted in the average"
  ],
  "nextSteps": "If you need to undo a declared conflict of interest, contact that competition's organizer for help.",
  "faq": [
    {
      "q": "Does the submission disappear from my list after I declare a conflict?",
      "a": "No. Its card stays in your assignment list — it's only marked with a \"Recused\" badge and can no longer be scored. This is intentional: it lets both you and the organizer see clearly that \"this assignment exists but has been recused,\" rather than having the record silently vanish."
    },
    {
      "q": "Am I the only one who can declare a conflict of interest?",
      "a": "Not necessarily. Besides declaring it yourself, an organizer/staff member with judge-management permission can also flag a conflict for you directly in the admin console, without you doing anything — both sources have the same effect and show the same badge/restriction on your assignment list."
    },
    {
      "q": "Can I undo a declaration myself?",
      "a": "There's currently no self-service undo interface. Once declared, the recusal record keeps blocking your scoring and file downloads; contact the organizer for help if you need it reversed."
    },
    {
      "q": "Does the conflict show up on the submission card?",
      "a": "Yes, as a \"Recused\" badge on the card. But a conflict of interest isn't itself a status value on the submission — it's a separately recorded recusal relationship, and the badge is just a real-time reflection of that record's existence."
    }
  ]
}
